import { WEBSHEET_TAGS } from '@whistler-ui/k2/constants';
import { setPromiseCall } from '@whistler-ui/k2/helpers';

import {
  createWebSheetLocalisations,
  createWebSheetSettings,
  deleteWebSheetLocalisation,
  getWebSheetLocalisations,
  getWebSheetSettings,
  updateWebSheetLocalisations,
  updateWebSheetSettings,
} from '../../services';
import { uploadLogo } from '../../services/uploadService';
import types from './types';

const actions = dispatch => {
  const handleError = (error, silent) => {
    dispatch({ type: types.SET_LOADING_STATUS, payload: false });
    if (silent) return null;
    throw error;
  };

  return {
    saveLogo: async ({ file } = {}) => {
      const resp = await uploadLogo(file);
      return resp;
    },
    getWebSheetsData: async ({
      environmentId,
      brandId,
      tag,
      silent = true,
    }) => {
      const params = { environmentId, brandId, tag };
      try {
        dispatch({ type: types.SET_LOADING_STATUS, payload: true });
        const localisations = (await getWebSheetLocalisations(params)) || [];
        const ws = await getWebSheetSettings(params);
        dispatch({
          type: types.SET_WEB_SHEETS_DATA,
          payload: { ...ws, localisations },
        });
        return ws;
      } catch (error) {
        return handleError(error, silent);
      }
    },
    saveWebSheetsData: async ({
      webSheet,
      oldLocalisations,
      silent = true,
    }) => {
      try {
        dispatch({ type: types.SET_LOADING_STATUS, payload: true });
        // Update/Create WebSheet settings
        if (webSheet.timestamp) {
          await updateWebSheetSettings(webSheet);
        } else {
          await createWebSheetSettings(webSheet);
        }

        // Update/Create WebSheet Localizations
        const promises = [];
        const hasLocaleChanged = verifyLocaleChange(oldLocalisations);
        webSheet.localisations.forEach(localisation => {
          const webLocale = { localisation, ...webSheet };
          if (localisation.timestamp && hasLocaleChanged(localisation)) {
            promises.push(
              setPromiseCall(updateWebSheetLocalisations, webLocale),
            );
          } else if (!localisation.timestamp) {
            promises.push(
              setPromiseCall(createWebSheetLocalisations, webLocale),
            );
          }
        });
        await Promise.all(promises);
        dispatch({ type: types.SET_WEB_SHEETS_DATA, payload: webSheet });
      } catch (error) {
        return handleError(error, silent);
      }
    },
    deleteWebSheetLocalisation: async ({ webSheet, locale, silent = true }) => {
      try {
        dispatch({ type: types.SET_LOADING_STATUS, payload: true });
        await deleteWebSheetLocalisation(webSheet, locale);
        dispatch({ type: types.SET_LOADING_STATUS, payload: false });
      } catch (error) {
        return handleError(error, silent);
      }
    },
    updateWebSheetData: ({ webSheet }) => {
      dispatch({ type: types.SET_WEB_SHEETS_DATA, payload: webSheet });
      return webSheet;
    },
    setLocaleContent: (tag, locale) => {
      if (tag === WEBSHEET_TAGS.WS_ESIM_QUICK_TRANSFER) {
        return {
          locale,
          data: {
            't&c': { title: '', content: [], agree: [], disagree: [] },
          },
        };
      } else if (tag === WEBSHEET_TAGS.WS_WATCH_ACTIVATE) {
        return {
          locale,
          data: {
            watch: {
              title: '',
              content: [],
              continue: [],
              checkboxLabel: [],
              link: [],
            },
            't&c': { title: '', content: [], agree: [], disagree: [] },
            'transfer_t&c': { title: '', content: [], agree: [], disagree: [] },
          },
        };
      } else if (tag === WEBSHEET_TAGS.WS_VOWIFI_ACTIVATE) {
        return {
          locale,
          data: {
            't&c': { title: '', content: [], agree: [], disagree: [] },
            emergency_address: {
              title: '',
              content: [],
              disclaimer: [],
            },
          },
        };
      }
    },
  };
};

const verifyLocaleChange = oldLocalisations => {
  const locales = oldLocalisations.reduce((map, locale) => {
    map[locale.locale] = locale;
    return map;
  }, {});

  return ({ locale, data }) => {
    return JSON.stringify(data) !== JSON.stringify(locales[locale]?.data);
  };
};

export default actions;
